/**
 * SubConfirmations — the sub confirmation handshake surface (UOW-5C #3791,
 * docs/features/unit-of-work.md §3.14).
 *
 * One shared section for the task peek and the subcontract peek: lists the
 * confirmations on the record (status pill, window, responded time), offers
 * the ONE send action ("Request confirmation" — compose contact + optional
 * window/note), and always surfaces the copyable public link so a PM can
 * text it when email is off (the prod kill-switch is by design).
 *
 * Facet rule: degrades to hidden on fetch failure — never blocks the peek.
 */
import React from 'react'
import { apiGet, apiPost, contacts as contactsApi, type Contact } from '@/lib/api'
import { Button } from '@/components/atoms/Button'
import { useToast } from '@/components/molecules/Toast'
import { CheckCircle, XCircle, Clock, RotateCw, Link2, Send } from 'lucide-react'

export interface SubConfirmation {
  id: number
  project_id: number
  task_id?: string
  task_name?: string
  contact_id?: number
  subcontract_id?: number
  requested_start?: string
  requested_end?: string
  status: 'pending' | 'confirmed' | 'declined' | 'stale'
  responded_at?: string
  note: string
  created_at: string
  sub_name: string
  sub_contact: string
  work_description: string
  portal_path?: string
  portal_url?: string
}

export const SUB_CONFIRM_STATUS: Record<SubConfirmation['status'], { label: string; tone: string; Icon: typeof Clock }> = {
  pending:   { label: 'Pending',   tone: 'in-progress', Icon: Clock },
  confirmed: { label: 'Confirmed', tone: 'completed',   Icon: CheckCircle },
  declined:  { label: 'Declined',  tone: 'blocked',     Icon: XCircle },
  stale:     { label: 'Stale',     tone: 'on-hold',     Icon: RotateCw },
}

export function SubConfirmStatusPill({ status }: { status: SubConfirmation['status'] }) {
  const cfg = SUB_CONFIRM_STATUS[status] || SUB_CONFIRM_STATUS.pending
  return (
    <span
      title={status === 'stale' ? 'The task moved after this was sent — re-send to ask again' : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 8px', borderRadius: 'var(--r-sm)',
        background: `var(--status-${cfg.tone}-bg)`, color: `var(--status-${cfg.tone}-tx)`,
        fontSize: 'var(--t-xs)', fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: `var(--status-${cfg.tone}-dot)` }} />
      {cfg.label}
    </span>
  )
}

export function fmtWindow(c: Pick<SubConfirmation, 'requested_start' | 'requested_end'>): string {
  if (!c.requested_start) return 'no dates'
  if (c.requested_end && c.requested_end !== c.requested_start) {
    return `${c.requested_start} → ${c.requested_end}`
  }
  return c.requested_start
}

async function copyLink(portalPath: string | undefined, portalURL: string | undefined): Promise<string | null> {
  const link = portalPath ? window.location.origin + portalPath : portalURL
  if (!link) return null
  try {
    await navigator.clipboard.writeText(link)
    return link
  } catch {
    return link // caller can still show it even if the clipboard write failed
  }
}

interface SubConfirmationsSectionProps {
  /** Send + list keyed to this task (the task peek). */
  taskId?: string
  /** Send + list keyed to this subcontract (the sub peek); id may be the public uuid. */
  subcontractId?: string
  /** Numeric project id — required with subcontractId for the list read. */
  projectId?: number
  /** Preselected contact for the request form (e.g. the subcontract's vendor). */
  defaultContactId?: number | string
  /** Hide the send affordance (view-only roles). */
  canSend?: boolean
  /** Section-shell wrapper — content only renders through it when there is
   *  something to show, so an empty read-only facet swallows itself
   *  (the §3.11 absence rule, same contract RelatedWorkSection follows). */
  renderSection?: (content: React.ReactNode) => React.ReactNode
}

/**
 * The section body. Thin by design: fetch, list, one send form.
 */
export function SubConfirmationsSection({ taskId, subcontractId, projectId, defaultContactId, canSend = true, renderSection }: SubConfirmationsSectionProps) {
  const { success, error: toastError } = useToast()
  const [items, setItems] = React.useState<SubConfirmation[] | null>(null)
  const [failed, setFailed] = React.useState(false)
  const [formOpen, setFormOpen] = React.useState(false)
  const [contactList, setContactList] = React.useState<Contact[]>([])
  const [contactRef, setContactRef] = React.useState<string>(defaultContactId ? String(defaultContactId) : '')
  const [start, setStart] = React.useState('')
  const [end, setEnd] = React.useState('')
  const [note, setNote] = React.useState('')
  const [sending, setSending] = React.useState(false)
  const [lastLink, setLastLink] = React.useState<string | null>(null)

  const listPath = taskId
    ? `/tasks/${taskId}/sub-confirmations`
    : projectId
      ? `/projects/${projectId}/sub-confirmations?subcontract_id=${encodeURIComponent(subcontractId || '')}`
      : null

  const load = React.useCallback(() => {
    if (!listPath) return
    apiGet(listPath)
      .then(d => { setItems(d.items || []); setFailed(false) })
      .catch(() => setFailed(true)) // facet rule: hidden, never block the peek
  }, [listPath])

  React.useEffect(() => { setItems(null); setLastLink(null); load() }, [load])

  React.useEffect(() => {
    if (!formOpen || contactList.length > 0) return
    contactsApi.list({ limit: 200 })
      .then(d => {
        const rows = (d.data || []) as Contact[]
        const rank = (c: Contact) => (c.type === 'subcontractor' || c.type === 'sub' ? 0 : c.type === 'vendor' || c.type === 'supplier' ? 1 : 2)
        rows.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
        setContactList(rows)
      })
      .catch(() => { /* picker just stays empty */ })
  }, [formOpen, contactList.length])

  const send = async () => {
    if (!contactRef) { toastError('Pick a contact to ask'); return }
    setSending(true)
    try {
      const body: Record<string, unknown> = { contact_id: contactRef }
      if (start) body.requested_start = start
      if (end) body.requested_end = end
      if (note.trim()) body.note = note.trim()
      const path = taskId
        ? `/tasks/${taskId}/send-confirmation`
        : `/subcontracts/${subcontractId}/send-confirmation`
      const res = await apiPost(path, body)
      const link = res.portal_path ? window.location.origin + res.portal_path : res.portal_url
      setLastLink(link || null)
      setFormOpen(false)
      setNote(''); setStart(''); setEnd('')
      success(res.email_queued ? 'Request sent — link ready to share too' : 'Request created — copy the link to share it')
      load()
    } catch (e) {
      toastError((e as Error).message || 'Could not send the request')
    } finally {
      setSending(false)
    }
  }

  const resend = async (c: SubConfirmation) => {
    setSending(true)
    try {
      const body: Record<string, unknown> = { contact_id: c.contact_id }
      const path = c.task_id
        ? `/tasks/${c.task_id}/send-confirmation`
        : `/subcontracts/${c.subcontract_id}/send-confirmation`
      const res = await apiPost(path, body)
      const link = res.portal_path ? window.location.origin + res.portal_path : res.portal_url
      setLastLink(link || null)
      success('Fresh request created — copy the link to share it')
      load()
    } catch (e) {
      toastError((e as Error).message || 'Could not re-send')
    } finally {
      setSending(false)
    }
  }

  const handleCopy = async (c: SubConfirmation) => {
    const link = await copyLink(c.portal_path, c.portal_url)
    if (link) success('Link copied — text it to the sub')
    else toastError('No link on this row yet — re-send to create one')
  }

  if (failed) return null
  if (items === null) return null // still loading — facets never flash spinners in the peek body
  // Absence renders nothing for viewers who can't send (§3.11 absence rule).
  if (items.length === 0 && !canSend) return null

  const inputStyle: React.CSSProperties = {
    padding: '6px 8px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)',
    background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 'var(--t-sm)', width: '100%',
    boxSizing: 'border-box',
  }

  const content = (
    <div>

      {items.map(c => (
        <div key={c.id} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0',
          borderBottom: '1px solid var(--border-subtle, var(--border))',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.sub_name || 'Unknown contact'}
            </div>
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)' }}>
              {fmtWindow(c)}
              {c.responded_at ? ` · answered ${new Date(c.responded_at).toLocaleDateString()}` : ''}
              {c.status === 'declined' && c.note ? ` · “${c.note}”` : ''}
            </div>
          </div>
          <SubConfirmStatusPill status={c.status} />
          {(c.portal_path || c.portal_url) && (
            <button onClick={() => handleCopy(c)} title="Copy public link"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2 }}>
              <Link2 size={14} />
            </button>
          )}
          {canSend && (c.status === 'stale' || c.status === 'declined') && (
            <button onClick={() => resend(c)} disabled={sending} title="Re-send (creates a fresh request)"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2 }}>
              <RotateCw size={14} />
            </button>
          )}
        </div>
      ))}

      {lastLink && (
        <div style={{
          marginTop: 8, padding: '8px 10px', borderRadius: 'var(--r-sm)',
          background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Link2 size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {lastLink}
          </span>
          <Button size="sm" variant="secondary" onClick={async () => {
            try { await navigator.clipboard.writeText(lastLink); success('Link copied') } catch { toastError('Copy failed — select the text instead') }
          }}>Copy</Button>
        </div>
      )}

      {canSend && !formOpen && (
        <div style={{ marginTop: 10 }}>
          <Button size="sm" variant="secondary" onClick={() => setFormOpen(true)}>
            <Send size={13} style={{ marginRight: 6 }} />
            Request confirmation
          </Button>
        </div>
      )}

      {canSend && formOpen && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <select value={contactRef} onChange={e => setContactRef(e.target.value)} style={inputStyle}>
            <option value="">Who are you asking?</option>
            {contactList.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.trade ? ` · ${c.trade}` : ''}{c.company ? ` (${c.company})` : ''}
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} style={inputStyle} title="Requested start (defaults to the task's dates)" />
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={inputStyle} title="Requested end (defaults to the task's dates)" />
          </div>
          <input type="text" placeholder="Optional note for the sub" value={note} onChange={e => setNote(e.target.value)} style={inputStyle} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={send} disabled={sending || !contactRef}>
              {sending ? 'Sending…' : 'Send request'}
            </Button>
          </div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)' }}>
            Emails when mail is on; either way you get a link you can text.
          </div>
        </div>
      )}
    </div>
  )
  return <>{renderSection ? renderSection(content) : content}</>
}
